import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { faArchive, faListAlt } from '@fortawesome/free-solid-svg-icons';
import { ReferralService, sharedApiClient } from '../fetcher';
import { downloadAllReferrals } from './referralCsvExport';
import {
  UserAbility,
  useNotifications,
  useAuth,
  IdentityService,
  TeamService,
  createCoalescer,
  isAbortError,
} from '@medprompt/shared';
import {
  ReferralViewMode,
  mergeFetchedReferral,
  referralBelongsInView,
  shouldFetchReferralUpdate,
  shouldRefreshReferralSearch,
} from './referralListMembership';

const VIEW_MODE_STORAGE_KEY = 'referralViewMode';

const useDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const toggle = () => setIsOpen((prev) => !prev);

  return { isOpen, setIsOpen, toggle, dropdownRef };
};

const useReferralList = () => {
  const { user } = useAuth();
  const {
    addNotification,
    addReferralUpdateCallback,
    addReconnectCallback,
  } = useNotifications();
  const listAbortRef = useRef(null);
  const patchAbortRef = useRef(null);
  const searchAbortRef = useRef(null);
  const searchRefreshAbortRef = useRef(null);
  const rerunSearchRef = useRef(() => {});
  const patchIdsRef = useRef(new Set());

  const hasDocsWrite = user?.authorities?.includes(UserAbility.DOCS_WRITE.key);

  const referralManager = useMemo(() => new ReferralService(sharedApiClient), []);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeUsers, setActiveUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [referralToDelete, setReferralToDelete] = useState(null);

  const [searchResults, setSearchResults] = useState([]);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [clearSearchBarFunction, setClearSearchBarFunction] = useState(null);

  const [viewMode, setViewMode] = useState(() => {
    return sessionStorage.getItem(VIEW_MODE_STORAGE_KEY) || ReferralViewMode.ACTIVE;
  });

  const viewModeDropdown = useDropdown();

  useEffect(() => {
    sessionStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  // Read through refs so landing data does not invalidate the fetch callback
  const referralsRef = useRef(referrals);
  referralsRef.current = referrals;
  const selectedReferralRef = useRef(selectedReferral);
  selectedReferralRef.current = selectedReferral;

  // No "already fetching" guard: it would drop the newest request while keeping the one the abort
  // below has just cancelled, leaving the list permanently empty. Newest request wins.
  const fetchReferrals = useCallback(async () => {
    const isInitialLoad = referralsRef.current.length === 0;
    if (isInitialLoad) setLoading(true);

    patchIdsRef.current.clear();
    patchAbortRef.current?.abort();
    listAbortRef.current?.abort();
    const controller = new AbortController();
    listAbortRef.current = controller;
    const options = { signal: controller.signal };

    try {
      let data;
      if (viewMode === ReferralViewMode.ARCHIVE) {
        data = await referralManager.fetchArchiveReferrals(options);
      } else {
        data = await referralManager.fetchActiveReferrals(options);
      }
      if (listAbortRef.current !== controller) {
        return; // a newer fetch has taken over
      }
      setReferrals(Array.isArray(data) ? data : []);
      rerunSearchRef.current();
    } catch (error) {
      if (isAbortError(error)) return;
      console.error('Failed to fetch referrals:', error);
      addNotification('fetch-referrals-error', 'Failed to fetch referrals', { isError: true });
    } finally {
      // Only the newest fetch owns the spinner, so an aborted one cannot leave it spinning.
      if (listAbortRef.current === controller) {
        listAbortRef.current = null;
        if (isInitialLoad) setLoading(false);
      }
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const identityManager = new IdentityService();
    identityManager.fetchActiveClinicUsers()
      .then((data) => setActiveUsers(data || []))
      .catch((err) => console.error('Failed to fetch active users', err));

    const teamManager = new TeamService();
    teamManager.fetchTeams()
      .then((data) => setTeams(data || []))
      .catch((err) => console.error('Failed to fetch teams', err));

    return () => {
      listAbortRef.current?.abort();
      patchAbortRef.current?.abort();
      searchAbortRef.current?.abort();
      searchRefreshAbortRef.current?.abort();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Covers mount and every view-mode change
  useEffect(() => {
    fetchReferrals();
  }, [viewMode, fetchReferrals]);

  const fetchReferralsRef = useRef(fetchReferrals);
  fetchReferralsRef.current = fetchReferrals;

  const listSyncRef = useRef({
    referrals,
    viewMode,
    searchResults,
    isSearchActive,
    searchQuery,
  });
  listSyncRef.current = {
    referrals,
    viewMode,
    searchResults,
    isSearchActive,
    searchQuery,
  };

  const patchReferrals = useCallback(async (referralIds) => {
    patchAbortRef.current?.abort();
    const controller = new AbortController();
    patchAbortRef.current = controller;

    const results = await Promise.all(referralIds.map(async (referralId) => {
      try {
        return await referralManager.fetchReferral(referralId, { signal: controller.signal });
      } catch (error) {
        return isAbortError(error) ? undefined : null;
      }
    }));

    if (controller.signal.aborted) return;
    if (patchAbortRef.current === controller) {
      patchAbortRef.current = null;
    }

    const { viewMode: mode, referrals: shown } = listSyncRef.current;
    const decisions = referralIds.map((referralId, index) => {
      const fetched = results[index];
      if (fetched === undefined) return { referralId, skip: true };
      const belongs = fetched == null ? false : referralBelongsInView(fetched, mode);
      return { referralId, fetched, belongs, skip: false };
    });

    setReferrals(previous => {
      let next = previous;
      decisions.forEach((decision) => {
        if (decision.skip) return;
        next = mergeFetchedReferral(next, decision.referralId, decision.fetched, decision.belongs);
      });
      return next;
    });

    decisions.forEach((decision) => {
      if (decision.skip) return;
      const stays = decision.belongs && (
        shown.some(referral => referral.referralId === decision.referralId) || decision.fetched != null
      );
      if (!stays) patchIdsRef.current.delete(decision.referralId);
    });
  }, [referralManager]);

  const patchHandlerRef = useRef(patchReferrals);
  useEffect(() => {
    patchHandlerRef.current = patchReferrals;
  }, [patchReferrals]);

  useEffect(() => {
    const ids = patchIdsRef.current;
    if (ids.size === 0) return;
    for (const id of ids) {
      if (referrals.some(referral => referral.referralId === id)) ids.delete(id);
    }
  }, [referrals]);

  const patchCoalescerRef = useRef(null);
  const searchCoalescerRef = useRef(null);
  useEffect(() => {
    patchCoalescerRef.current = createCoalescer((ids) => patchHandlerRef.current(ids));
    searchCoalescerRef.current = createCoalescer(() => rerunSearchRef.current());
    return () => {
      patchCoalescerRef.current?.cancel();
      searchCoalescerRef.current?.cancel();
    };
  }, []);

  useEffect(() => {
    if (!addReferralUpdateCallback) return undefined;

    const handleReferralUpdate = (referralId, statusTransition) => {
      if (!referralId) return;
      if (statusTransition?.to === 'NON_EXISTENT') {
        patchIdsRef.current.delete(referralId);
        if (selectedReferralRef.current?.referralId === referralId) {
          setModalOpen(false);
          setSelectedReferral(null);
        }
        setReferrals(previous => previous.filter(referral => referral.referralId !== referralId));
        setSearchResults(previous => previous.filter(referral => referral.referralId !== referralId));
        return;
      }
      const { referrals: shown, viewMode: mode, searchResults: results, isSearchActive: searching } = listSyncRef.current;
      const isTracked = shown.some(referral => referral.referralId === referralId)
        || patchIdsRef.current.has(referralId);
      if (shouldFetchReferralUpdate(isTracked, statusTransition, mode)) {
        patchIdsRef.current.add(referralId);
        patchCoalescerRef.current?.push(referralId);
      }
      const inSearch = results.some(referral => referral.referralId === referralId);
      if (searching && shouldRefreshReferralSearch(inSearch, statusTransition)) {
        searchCoalescerRef.current?.push(referralId);
      }
    };

    return addReferralUpdateCallback(handleReferralUpdate);
  }, [addReferralUpdateCallback]);

  useEffect(() => {
    if (!addReconnectCallback) return;
    return addReconnectCallback(() => fetchReferralsRef.current());
  }, [addReconnectCallback]);

  const viewModeOptions = [
    { name: 'Active', value: ReferralViewMode.ACTIVE, icon: faListAlt },
    { name: 'Archive', value: ReferralViewMode.ARCHIVE, icon: faArchive }
  ];

  const currentViewModeOption = viewModeOptions.find((o) => o.value === viewMode) || viewModeOptions[0];

  const handleViewModeChange = (option) => {
    setViewMode(option.value);
    viewModeDropdown.setIsOpen(false);
  };

  const openReferral = (referral) => {
    setSelectedReferral(referral);
    setModalOpen(true);
  };

  const closeReferral = () => {
    setModalOpen(false);
    setSelectedReferral(null);
  };

  const handleCreateReferral = async (request) => {
    setCreateLoading(true);
    try {
      await referralManager.createReferral(request);
      setCreateModalOpen(false);
      // SSE will trigger refresh
    } catch (error) {
      console.error('Failed to create referral:', error);
      addNotification('referral-create-error', 'Failed to create referral', { isError: true });
    } finally {
      setCreateLoading(false);
    }
  };

  // Both the list and the search results feed the table, so a patch has to reach both.
  //
  // Deliberately does not touch `selectedReferral`: ReferralModal resets its edit fields and
  // clears edit mode from the `referral` prop on identity change, so patching it would drop the
  // user out of the textarea mid-edit. Reopening reads a fresh row from the collections below.
  const applyReferralPatch = useCallback((referralId, fields) => {
    const patchMatching = (collection) => collection.map(referral => (
      referral.referralId === referralId ? { ...referral, ...fields } : referral
    ));
    setReferrals(patchMatching);
    setSearchResults(patchMatching);
  }, []);

  const removeReferralLocally = useCallback((referralId) => {
    const withoutReferral = (collection) => collection.filter(
      referral => referral.referralId !== referralId
    );
    setReferrals(withoutReferral);
    setSearchResults(withoutReferral);
  }, []);

  const handleUpdateReferral = async (update) => {
    setModalLoading(true);
    try {
      await referralManager.updateReferral(update);
      const { referralId, ...fields } = update;
      applyReferralPatch(referralId, fields);
      setModalLoading(false);
    } catch (error) {
      console.error('Failed to update referral:', error);
      setModalLoading(false);
      addNotification('referral-update-error', 'Failed to update referral', { isError: true });
      throw error;
    }
  };

  const handleMarkComplete = async (referralId) => {
    setModalLoading(true);
    try {
      await referralManager.completeReferral(referralId);
      applyReferralPatch(referralId, { isComplete: true });
      setModalLoading(false);
      setModalOpen(false);
    } catch (error) {
      console.error('Failed to complete referral:', error);
      setModalLoading(false);
      addNotification('referral-complete-error', 'Failed to mark referral as complete', { isError: true });
    }
  };

  const handleDeleteReferral = (referralId) => {
    const referral = (isSearchActive ? searchResults : referrals)
      .find((item) => item.referralId === referralId);
    if (!referral) return;
    setReferralToDelete(referral);
    setDeleteModalOpen(true);
  };

  const confirmDeleteReferral = async () => {
    if (!referralToDelete) return;
    setDeleteLoading(true);
    try {
      await referralManager.deleteReferral(referralToDelete.referralId);
      removeReferralLocally(referralToDelete.referralId);
      setDeleteLoading(false);
      setDeleteModalOpen(false);
      setReferralToDelete(null);
      setModalOpen(false);
      setSelectedReferral(null);
    } catch (error) {
      console.error('Failed to delete referral:', error);
      setDeleteLoading(false);
      addNotification('referral-delete-error', 'Failed to delete referral', { isError: true });
    }
  };

  // Cancels the previous query so results always match what is on screen, rather than whichever
  // response happens to arrive last.
  const rerunSearch = useCallback(async () => {
    const { isSearchActive: searching, searchQuery: query } = listSyncRef.current;
    if (!searching || !query?.trim()) return;

    searchRefreshAbortRef.current?.abort();
    const controller = new AbortController();
    searchRefreshAbortRef.current = controller;

    try {
      const results = await referralManager.searchReferrals(query, { signal: controller.signal });
      if (searchRefreshAbortRef.current !== controller) return;
      const current = listSyncRef.current;
      if (!current.isSearchActive || current.searchQuery !== query) return;
      setSearchResults(Array.isArray(results) ? results : []);
    } catch (error) {
      if (!isAbortError(error)) {
        console.error('Error refreshing referral search:', error);
      }
    } finally {
      if (searchRefreshAbortRef.current === controller) {
        searchRefreshAbortRef.current = null;
      }
    }
  }, [referralManager]);
  rerunSearchRef.current = rerunSearch;

  const handleReferralSearch = useCallback(async (query, callback) => {
    searchAbortRef.current?.abort();
    searchRefreshAbortRef.current?.abort();

    if (!query || !query.trim()) {
      searchAbortRef.current = null;
      setSearchResults([]);
      setIsSearchActive(false);
      setSearchQuery('');
      if (callback) {
        callback([]);
      }
      return;
    }

    const controller = new AbortController();
    searchAbortRef.current = controller;

    try {
      const results = await referralManager.searchReferrals(query, { signal: controller.signal });
      if (searchAbortRef.current !== controller) {
        return; // a newer query has taken over
      }
      setSearchResults(Array.isArray(results) ? results : []);
      setIsSearchActive(true);
      setSearchQuery(query);
      if (callback) {
        callback(results);
      }
    } catch (error) {
      if (isAbortError(error)) {
        return; // superseded; the newer query owns the results
      }
      console.error('Error searching referrals:', error);
      setSearchResults([]);
      setIsSearchActive(false);
      setSearchQuery('');
      if (callback) {
        callback([]);
      }
    } finally {
      if (searchAbortRef.current === controller) {
        searchAbortRef.current = null;
      }
    }
  }, [referralManager]);

  const handleClearSearch = () => {
    searchRefreshAbortRef.current?.abort();
    setSearchResults([]);
    setIsSearchActive(false);
    setSearchQuery('');
    if (clearSearchBarFunction) {
      clearSearchBarFunction();
    }
  };

  const displayReferrals = useMemo(() => {
    if (isSearchActive) return searchResults;
    return referrals.filter(referral => referralBelongsInView(referral, viewMode));
  }, [isSearchActive, searchResults, referrals, viewMode]);

  const handleDownloadAllReferrals = async () => {
    try {
      const count = await downloadAllReferrals(referralManager);
      addNotification('referrals-download-success', `Downloaded ${count} referrals`);
    } catch (error) {
      console.error('Failed to download referrals:', error);
      addNotification('referrals-download-error', 'Failed to download referrals', { isError: true });
    }
  };

  return {
    ReferralViewMode,
    referralManager,
    addNotification,
    applyReferralPatch,
    hasDocsWrite,
    referrals,
    loading,
    activeUsers,
    teams,
    selectedReferral,
    modalOpen,
    modalLoading,
    createModalOpen,
    setCreateModalOpen,
    createLoading,
    deleteModalOpen,
    setDeleteModalOpen,
    deleteLoading,
    referralToDelete,
    setReferralToDelete,
    isSearchActive,
    searchResults,
    searchQuery,
    setClearSearchBarFunction,
    viewMode,
    viewModeDropdown,
    viewModeOptions,
    currentViewModeName: currentViewModeOption.name,
    currentViewModeIcon: currentViewModeOption.icon,
    handleViewModeChange,
    openReferral,
    closeReferral,
    handleCreateReferral,
    handleUpdateReferral,
    handleMarkComplete,
    handleDeleteReferral,
    confirmDeleteReferral,
    handleReferralSearch,
    handleClearSearch,
    handleDownloadAllReferrals,
    displayReferrals,
  };
};

export default useReferralList;
